// src/components/operationCenter/widgets/MacroKeyMetricsWidget.tsx
// Table-style Macro Key Metrics widget showing all values from macro overview

import React, { useMemo, useState } from "react";
import { Globe } from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";
import { useMacroOverview } from "@/hooks/macro/useMacroOverview";
import type { MacroOverview } from "@/types/dashboards/macro";
import {
  formatMacroValue,
  formatMacroPercentChange,
  getIndicatorDisplayName,
} from "@/utils/macroFormatters";

type TimeRange = "1m" | "3m" | "6m" | "1y" | "4y";

// Time range picker component
const TimeRangePicker: React.FC<{
  value: TimeRange;
  onChange: (value: TimeRange) => void;
}> = ({ value, onChange }) => {
  const options: { key: TimeRange; label: string }[] = [
    { key: "1m", label: "1M" },
    { key: "3m", label: "3M" },
    { key: "6m", label: "6M" },
    { key: "1y", label: "1Y" },
    { key: "4y", label: "4Y" },
  ];

  return (
    <div className="flex gap-0.5">
      {options.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-all ${
            value === key
              ? "bg-blue-500/20 text-blue-400"
              : "text-slate-400 hover:text-slate-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// Get change value based on time range
const getChange = (metric: MacroOverview, timeRange: TimeRange): number | null => {
  switch (timeRange) {
    case "1m": return metric.change_1m;
    case "3m": return metric.change_3m;
    case "6m": return metric.change_6m;
    case "1y": return metric.change_1y;
    case "4y": return metric.change_4y;
    default: return metric.change_1m;
  }
};

// Get row background styling based on index
const getRowBgClass = (idx: number): string => {
  if (idx === 0) return "bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent border-l-2 border-blue-500/40";
  if (idx === 1) return "bg-gradient-to-r from-slate-400/8 via-slate-400/3 to-transparent border-l-2 border-slate-400/30";
  if (idx === 2) return "bg-gradient-to-r from-slate-500/6 via-slate-500/2 to-transparent border-l-2 border-slate-500/25";
  return "odd:bg-slate-800/20 border-l-2 border-transparent";
};

// Curated list of indicators to show, in display order
const PRIORITY_ORDER = [
  // Market Risk & Volatility
  "DXY",
  "VIX",
  // Equities
  "SP500",
  "NASDAQ",
  // Commodities
  "GOLD_FUTURES_USD",
  "OIL_WTI",
  // Fed & Liquidity
  "US_CB_BALANCE_SHEET",
  "US_M2_MONEY_SUPPLY",
  "US_NET_LIQUIDITY",
  "US_FED_FUNDS_EFFECTIVE",
  "US_CB_LIQUIDITY_SWAPS",
  // Treasuries (short to long)
  "US_TREASURY_YIELD_2Y",
  "US_TREASURY_YIELD_10Y",
  "US_TREASURY_YIELD_30Y",
  // Credit Spreads
  "US_OAS",
  // Inflation
  "US_CPI",
  "EUROZONE_CPI",
];

const PRIORITY_SET = new Set(PRIORITY_ORDER);

const MacroKeyMetricsWidget: React.FC<WidgetProps> = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>("1m");
  const { data, isLoading, error } = useMacroOverview();

  // Transform and sort metrics
  const metrics = useMemo(() => {
    if (!data) return [];

    return data
      // Only show curated indicators
      .filter((item) => item.current_value !== null && PRIORITY_SET.has(item.indicator_code))
      .sort((a, b) => {
        const aIndex = PRIORITY_ORDER.indexOf(a.indicator_code);
        const bIndex = PRIORITY_ORDER.indexOf(b.indicator_code);
        return aIndex - bIndex;
      });
  }, [data]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Globe className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">{(error as Error).message}</div>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Globe className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No macro data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-800/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Macro Indicators
          </span>
        </div>
        <TimeRangePicker value={timeRange} onChange={setTimeRange} />
      </div>

      {/* Column Headers */}
      <div className="grid grid-cols-[32%_38%_30%] gap-1 items-center px-3 py-1.5 text-[10px] font-medium text-slate-400 border-b border-slate-700/30 flex-shrink-0 bg-slate-800/20">
        <div>Indicator</div>
        <div className="text-right">Value</div>
        <div className="text-right">Chg</div>
      </div>

      {/* Metrics List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {metrics.map((metric, idx) => {
          const change = getChange(metric, timeRange);
          const isPositive = change !== null && change >= 0;
          const changeColor = change === null
            ? "text-slate-500"
            : isPositive
              ? "text-emerald-400"
              : "text-rose-400";

          return (
            <div
              key={metric.indicator_code}
              className={`${getRowBgClass(idx)} transition-all duration-150 hover:bg-slate-700/20`}
            >
              <div className="grid grid-cols-[32%_38%_30%] gap-1 items-center py-1.5 px-3">
                {/* Indicator name */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`text-[11px] truncate ${idx < 3 ? "text-white font-medium" : "text-slate-200"}`}>
                    {getIndicatorDisplayName(metric.indicator_code)}
                  </span>
                </div>

                {/* Current Value */}
                <div className="text-right tabular-nums text-[11px] text-slate-100 font-medium">
                  {formatMacroValue(metric.current_value, metric.indicator_code)}
                </div>

                {/* Change */}
                <div className={`text-right tabular-nums text-[11px] font-semibold ${changeColor}`}>
                  {formatMacroPercentChange(change)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-slate-700/30 bg-slate-800/20 flex-shrink-0">
        <div className="text-[10px] text-slate-500 text-center">
          {metrics.length} indicators • {timeRange.toUpperCase()} change
        </div>
      </div>
    </div>
  );
};

export default MacroKeyMetricsWidget;
